import React, { useState, useEffect, useRef } from 'react';
import { HiClock } from 'react-icons/hi';
import { parseTimeInput, formatTimeForDisplay, validateTimeRange } from '../../../utils/timeFormatters';
import { useGeneral } from '../../../contexts';
import Button from '../Button/Button';
import styles from './TimePicker.module.css';

export interface TimePickerProps {
  value: string; // HH:mm format
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  minuteStep?: number; // Not used in simple input mode, kept for API compatibility
  format?: '12h' | '24h'; // default: '24h'
  locale?: string;
  className?: string;
  disabled?: boolean;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  placeholder,
  label,
  error = false,
  minuteStep = 1,
  format = '24h',
  locale,
  className = '',
  disabled = false,
}) => {
  const { language } = useGeneral();
  const { getTranslation } = language;
  const currentLocale = locale || language.language;
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState<string>('');
  const [minutes, setMinutes] = useState<string>('');
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const containerRef = useRef<HTMLDivElement>(null);
  const hoursInputRef = useRef<HTMLInputElement>(null);

  // Parse initial value when dropdown opens or value changes
  useEffect(() => {
    if (value) {
      const parsed = parseTimeInput(value);
      if (parsed) {
        if (format === '12h') {
          const displayHours = parsed.hours === 0 ? 12 : parsed.hours > 12 ? parsed.hours - 12 : parsed.hours;
          setHours(String(displayHours));
          setPeriod(parsed.hours >= 12 ? 'PM' : 'AM');
        } else {
          setHours(String(parsed.hours).padStart(2, '0'));
        }
        setMinutes(String(parsed.minutes).padStart(2, '0'));
      }
    } else {
      setHours('');
      setMinutes('');
      setPeriod('AM');
    }
  }, [value, format]);

  // When dropdown opens, parse current value and focus input
  useEffect(() => {
    if (isOpen && value) {
      const parsed = parseTimeInput(value);
      if (parsed) {
        if (format === '12h') {
          const displayHours = parsed.hours === 0 ? 12 : parsed.hours > 12 ? parsed.hours - 12 : parsed.hours;
          setHours(String(displayHours));
          setPeriod(parsed.hours >= 12 ? 'PM' : 'AM');
        } else {
          setHours(String(parsed.hours).padStart(2, '0'));
        }
        setMinutes(String(parsed.minutes).padStart(2, '0'));
      }
    } else if (isOpen && !value) {
      setHours('');
      setMinutes('');
      setPeriod('AM');
    }
    
    // Focus the hours input when picker opens
    if (isOpen && hoursInputRef.current) {
      setTimeout(() => {
        hoursInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen, value, format]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle hours input
  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, ''); // Only numbers
    if (format === '12h') {
      if (val === '' || (parseInt(val) >= 1 && parseInt(val) <= 12)) {
        setHours(val);
      }
    } else {
      if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 23)) {
        setHours(val);
      }
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  // Handle minutes input
  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, ''); // Only numbers
    if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 59)) {
      setMinutes(val);
    }
  };

  // Handle period toggle (AM/PM)
  const handlePeriodToggle = () => {
    setPeriod(period === 'AM' ? 'PM' : 'AM');
  };

  // Handle save
  const handleSave = () => {
    const hoursNum = parseInt(hours) || 0;
    const minutesNum = parseInt(minutes) || 0;

    if (format === '12h') {
      // Convert 12h to 24h format
      let hours24 = hoursNum;
      if (period === 'PM' && hoursNum !== 12) {
        hours24 = hoursNum + 12;
      } else if (period === 'AM' && hoursNum === 12) {
        hours24 = 0;
      }

      if (validateTimeRange(hours24, minutesNum)) {
        const hoursStr = String(hours24).padStart(2, '0');
        const minutesStr = String(minutesNum).padStart(2, '0');
        onChange(`${hoursStr}:${minutesStr}`);
        setIsOpen(false);
      }
    } else {
      if (validateTimeRange(hoursNum, minutesNum)) {
        const hoursStr = String(hoursNum).padStart(2, '0');
        const minutesStr = String(minutesNum).padStart(2, '0');
        onChange(`${hoursStr}:${minutesStr}`);
        setIsOpen(false);
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsOpen(false);
    // Reset to current value
    if (value) {
      const parsed = parseTimeInput(value);
      if (parsed) {
        if (format === '12h') {
          const displayHours = parsed.hours === 0 ? 12 : parsed.hours > 12 ? parsed.hours - 12 : parsed.hours;
          setHours(String(displayHours));
          setPeriod(parsed.hours >= 12 ? 'PM' : 'AM');
        } else {
          setHours(String(parsed.hours).padStart(2, '0'));
        }
        setMinutes(String(parsed.minutes).padStart(2, '0'));
      }
    }
  };

  // Format display value
  const displayValue = value ? formatTimeForDisplay(value, format, currentLocale) : '';

  // Check if input is valid
  const isValid = () => {
    const hoursNum = parseInt(hours) || 0;
    const minutesNum = parseInt(minutes) || 0;
    
    if (format === '12h') {
      const hours24 = period === 'PM' && hoursNum !== 12 ? hoursNum + 12 : (period === 'AM' && hoursNum === 12 ? 0 : hoursNum);
      return validateTimeRange(hours24, minutesNum);
    }
    return validateTimeRange(hoursNum, minutesNum);
  };

  return (
    <div className={`${styles.timePickerContainer} ${className}`} ref={containerRef}>
      {label && (
        <label className={styles.label}>
          {label}
        </label>
      )}
      <div className={styles.inputWrapper}>
        <input
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder || (format === '12h' ? 'HH:mm AM/PM' : 'HH:mm')}
          className={`${styles.input} ${error ? styles.inputError : ''} ${disabled ? styles.inputDisabled : ''}`}
          onClick={() => !disabled && setIsOpen(true)}
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.clockIcon}
          onClick={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          aria-label="Open time picker"
        >
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
          {/* @ts-ignore - react-icons compatibility with React 19 */}
          <HiClock size={18} />
        </button>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownContent}>
            <div className={styles.timeInputs}>
              <div className={styles.timeInputGroup}>
                <label className={styles.timeLabel}>
                  {getTranslation('events.hours') || 'Hours'}
                </label>
                <input
                  ref={hoursInputRef}
                  type="text"
                  className={styles.timeInput}
                  value={hours}
                  onChange={handleHoursChange}
                  onKeyDown={handleKeyDown}
                  placeholder={format === '12h' ? '1-12' : '0-23'}
                  maxLength={2}
                />
              </div>

              <div className={styles.separator}>:</div>

              <div className={styles.timeInputGroup}>
                <label className={styles.timeLabel}>
                  {getTranslation('events.minutes') || 'Minutes'}
                </label>
                <input
                  type="text"
                  className={styles.timeInput}
                  value={minutes}
                  onChange={handleMinutesChange}
                  onKeyDown={handleKeyDown}
                  placeholder="00-59"
                  maxLength={2}
                />
              </div>

              {format === '12h' && (
                <>
                  <div className={styles.separator}></div>
                  <div className={styles.timeInputGroup}>
                    <label className={styles.timeLabel}>
                      {getTranslation('events.am') || 'AM'} / {getTranslation('events.pm') || 'PM'}
                    </label>
                    <button
                      type="button"
                      className={`${styles.periodButton} ${period === 'AM' ? styles.periodButtonActive : ''}`}
                      onClick={handlePeriodToggle}
                    >
                      {period}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className={styles.dropdownActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancel}
              >
                {getTranslation('common.cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={!hours || !minutes || !isValid()}
              >
                {getTranslation('common.save') || 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
